import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { WorkOrderOtherCostItem } from 'gql/graphql';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { getInitialState } from '../../common/addOtherHelpers';

export const useAddOtherData = ({ workOrderOthers }: { workOrderOthers: Partial<WorkOrderOtherCostItem>[] }) => {
    const [visibleRowIds, setVisibleRowIds] = useState<Record<number, boolean>>({});
    const [filterState, setFilterState] = useState<string[]>([]);

    const changeFilterHandler = useCallback(({ checkedValues }: any) => {
        setFilterState(checkedValues);
    }, []);

    const rows = useMemo(() => {
        const filteredOthers = filterState?.length
            ? workOrderOthers.filter((other) =>
                  other?.otherCost?.name ? filterState?.includes(other?.otherCost?.name) : true,
              )
            : workOrderOthers;

        return filteredOthers
            ?.map((other) => [
                {
                    ...other,
                    ...other?.otherCost,
                    id: other?.otherCost?.contractOtherCostId,
                    nestLevel: 0,
                },
                ...(other.workOrderObjects ?? []).map((obj) => ({
                    ...obj,
                    id: `${other?.otherCost?.contractOtherCostId}_${obj.workOrderRootObjectId}`,
                    objectId: obj.workOrderRootObjectId,
                    parentId: other?.otherCost?.contractOtherCostId,
                })),
            ])
            .flat();
    }, [filterState, workOrderOthers]);

    const visibleRows = useMemo(
        () =>
            rows?.filter(
                (row) =>
                    (row && 'parentId' in row && visibleRowIds[row.parentId]) ||
                    (row && 'nestLevel' in row && row?.nestLevel === 0),
            ),
        [rows, visibleRowIds],
    );

    const changeVisibleRowIds = useCallback(
        (rowId: number) => () => {
            setVisibleRowIds((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
        },
        [],
    );

    const nameFilter = useMemo<ColDef>(
        () => ({
            filter: AgGridCustomFilterAdapter,
            filterParams: {
                changeFilterHandler,
                filterItems: workOrderOthers?.map((item) => ({
                    label: item?.otherCost?.name,
                })),
                columns: [
                    {
                        headerName: 'Наименование',
                        field: 'label',
                        checkboxSelection: true,
                        width: 400,
                    },
                ],
                isClientSideSearch: true,
                childFieldAccessor: 'label',
            },
        }),
        [changeFilterHandler, workOrderOthers],
    );

    const initialState = useMemo(() => getInitialState(workOrderOthers), [workOrderOthers]);

    return useMemo(
        () => ({
            groupedData: rows,
            visibleRows,
            visibleRowIds,
            changeVisibleRowIds,
            nameFilter,
            initialState,
        }),
        [changeVisibleRowIds, nameFilter, rows, visibleRowIds, visibleRows, initialState],
    );
};
